import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { CLIENT_TYPE_LABELS, clientTypeOfAge, coarseDisorderGroupOf, disorderDrillLabelOf } from "../shared/disorderGroups";
import type { ClientTypeKey, CoarseDisorderGroupKey } from "../shared/disorderGroups";
import { FINISHED_STATUSES, SUCCESS_STATUSES, successRatePct } from "../shared/sharedMetrics";
import { ACTIVE_USER_STATUS } from "./teamKpisConfig";
import { hasOngoingSpanOfService, SpansByTreatment } from "./teamKpisSpans";
import { normalizePersonName, ownerIsTherapistOfTreatment } from "./teamKpisRows";

// Grupos de perturbação e tipo de cliente vivem agora em shared/disorderGroups (usados por 2+ dashboards);
// re-exportados aqui para os importadores existentes (TeamKpisDashboard, teamKpisActives, testes) não mudarem.
export { COARSE_DISORDER_GROUPS, coarseDisorderGroupOf, coarseDisorderGroupLabel, disorderDrillLabelOf, CLIENT_TYPE_LABELS, clientTypeOfAge } from "../shared/disorderGroups";
export type { CoarseDisorderGroupKey, ClientTypeKey } from "../shared/disorderGroups";

// Tratamentos terminados por gestora de caso E por psicóloga (substituem a série "% de sucesso").
// Gestoras: estado terminado (sucesso/desistência), com gestora, ≥1 sessão NFB realizada e SEM a
// gestora como psicóloga do tratamento (esses pertencem à tabela das psicólogas). Psicólogas:
// estado terminado, ≥1 sessão de Psicologia realizada, crédito pelas terapeutas do tratamento —
// um tratamento com várias psicólogas conta para cada uma. O mesmo predicado alimenta a tabela
// E o drill-down — a modal reconcilia com o widget por construção.

type OutcomePersonUser = { id: string; first_name: string | null; last_name: string | null; status?: string | null };

// Campos comuns aos dois universos; as variantes acrescentam a atribuição (owner vs terapeutas expandidas).
export type OutcomeTreatmentBase = {
    id: number | string;
    treatment_status_new: string | null;
    age_when_started: number | null;
    date_of_start: string | null;
    date_if_finish: string | null;
    diagnose_main?: { id: number | string; name?: string | null } | null;
    client?: { client_id?: string | null } | null;
    sessions?: Array<{ service: number | null; status: string | null; start_date?: string | null }> | null;
};

export type OutcomeTreatmentItem = OutcomeTreatmentBase & {
    client_owner?: {
        id?: number | string;
        user_id?: OutcomePersonUser | null;
    } | null;
    theurapists_involved_in_treamtent?: Array<{ technicians_id: number | string | null }> | null;
};

export type PsyOutcomeTreatmentItem = OutcomeTreatmentBase & {
    theurapists_involved_in_treamtent?: Array<{
        technicians_id: { id?: number | string; user_id?: OutcomePersonUser | null } | null;
    }> | null;
};

export type OutcomeFilters = { groupKey: CoarseDisorderGroupKey | null; clientType: ClientTypeKey | null };

const doneServiceSessionsCount = (treatment: OutcomeTreatmentBase, service: number) => {
    const sessionList = treatment.sessions || [];
    const count = sessionList.filter((session) => session.status === "done" && Number(session.service) === service).length;
    return count;
};

const hasFinishedStatus = (treatment: OutcomeTreatmentBase) => {
    const finished = Boolean(treatment.treatment_status_new && FINISHED_STATUSES.includes(treatment.treatment_status_new));
    return finished;
};

const ACTIVE_TREATMENT_STATUS = "all_good";

// Coluna "Ativos": estado "Ativo" do tratamento E o período de services_provided da disciplina da
// tabela ainda "A Decorrer". As duas condições são necessárias — o estado sozinho conta quem já
// terminou o NFB (a gestora deixou de o gerir), e o span sozinho contaria tratamentos terminados cujo
// período nunca foi fechado (date_end em branco).
const hasActiveStatusOfService = (treatment: OutcomeTreatmentBase, spansByTreatment: SpansByTreatment, service: number) => {
    if (treatment.treatment_status_new !== ACTIVE_TREATMENT_STATUS) return false;
    const active = hasOngoingSpanOfService(spansByTreatment[Number(treatment.id)], service);
    return active;
};

// Colunas das tabelas: os itens do pivot misturam terminados (seguem o filtro "Período") e ativos
// (fotografia atual) — as medidas separam por estado.
export const finishedOutcomeTreatmentsOf = <TreatmentType extends OutcomeTreatmentBase>(treatments: Array<TreatmentType>) => {
    const finished = treatments.filter(hasFinishedStatus);
    return finished;
};

export const activeOutcomeTreatmentsOf = <TreatmentType extends OutcomeTreatmentBase>(
    treatments: Array<TreatmentType>,
    spansByTreatment: SpansByTreatment,
    service: number
) => {
    const active = treatments.filter((treatment) => hasActiveStatusOfService(treatment, spansByTreatment, service));
    return active;
};

const matchesOutcomeFilters = (treatment: OutcomeTreatmentBase, filters: OutcomeFilters) => {
    if (filters.groupKey !== null && coarseDisorderGroupOf(treatment.diagnose_main?.id) !== filters.groupKey) return false;
    if (filters.clientType !== null && clientTypeOfAge(treatment.age_when_started) !== filters.clientType) return false;
    return true;
};

// Predicado ÚNICO de elegibilidade das gestoras + filtros da seleção (grupo/tipo); o estado
// (terminado vs ativo) é o único ponto que varia entre as colunas de resultados e a coluna Ativos.
const ownerTreatmentsMatchingByStatus = (
    treatmentItems: Array<OutcomeTreatmentItem>,
    filters: OutcomeFilters,
    statusMatches: (treatment: OutcomeTreatmentBase) => boolean
) => {
    const matching = treatmentItems.filter((treatment) => {
        const ownerUser = treatment.client_owner?.user_id;
        if (!ownerUser) return false;
        if (!statusMatches(treatment)) return false;
        if (doneServiceSessionsCount(treatment, NFB_SERVICE) === 0) return false;
        if (ownerIsTherapistOfTreatment(treatment)) return false;
        const matches = matchesOutcomeFilters(treatment, filters);
        return matches;
    });
    return matching;
};

export const outcomeTreatmentsMatching = (treatmentItems: Array<OutcomeTreatmentItem>, filters: OutcomeFilters) => {
    const matching = ownerTreatmentsMatchingByStatus(treatmentItems, filters, hasFinishedStatus);
    return matching;
};

export const activeOwnerTreatmentsMatching = (
    treatmentItems: Array<OutcomeTreatmentItem>,
    filters: OutcomeFilters,
    spansByTreatment: SpansByTreatment
) => {
    const matching = ownerTreatmentsMatchingByStatus(treatmentItems, filters, (treatment) =>
        hasActiveStatusOfService(treatment, spansByTreatment, NFB_SERVICE)
    );
    return matching;
};

export const outcomeOwnerNameOf = (treatment: OutcomeTreatmentItem) => {
    const ownerUser = treatment.client_owner?.user_id;
    const personName = normalizePersonName(ownerUser?.first_name, ownerUser?.last_name);
    return personName;
};

// Âmbito das pessoas (universo + filtro "Utilizadores" + contas excluídas) — alimenta o pivot
// Gestora → Perturbação; as medidas e o drill derivam dos itens de cada nó, por isso reconciliam.
const ownerPersonScope = (treatments: Array<OutcomeTreatmentItem>, includeSuspended: boolean, excludedPersonNames: Array<string>) => {
    const scoped = treatments.filter((treatment) => {
        const ownerUser = treatment.client_owner!.user_id!;
        if (excludedPersonNames.includes(outcomeOwnerNameOf(treatment))) return false;
        const included = includeSuspended || ownerUser.status === ACTIVE_USER_STATUS;
        return included;
    });
    return scoped;
};

export const scopedOutcomeTreatments = (
    treatmentItems: Array<OutcomeTreatmentItem>,
    filters: OutcomeFilters,
    includeSuspended: boolean,
    excludedPersonNames: Array<string>
) => {
    const scoped = ownerPersonScope(outcomeTreatmentsMatching(treatmentItems, filters), includeSuspended, excludedPersonNames);
    return scoped;
};

export const scopedActiveOwnerTreatments = (
    treatmentItems: Array<OutcomeTreatmentItem>,
    filters: OutcomeFilters,
    includeSuspended: boolean,
    excludedPersonNames: Array<string>,
    spansByTreatment: SpansByTreatment
) => {
    const scoped = ownerPersonScope(
        activeOwnerTreatmentsMatching(treatmentItems, filters, spansByTreatment),
        includeSuspended,
        excludedPersonNames
    );
    return scoped;
};

// Crédito das psicólogas: 1 item por (tratamento, psicóloga) — junções duplicadas contam uma vez.
export type PsyOutcomeCredit = {
    personKey: string;
    personName: string;
    personActive: boolean;
    treatment: PsyOutcomeTreatmentItem;
};

const psyCreditsMatchingByStatus = (
    treatmentItems: Array<PsyOutcomeTreatmentItem>,
    filters: OutcomeFilters,
    statusMatches: (treatment: OutcomeTreatmentBase) => boolean
) => {
    const credits: Array<PsyOutcomeCredit> = [];
    treatmentItems.forEach((treatment) => {
        if (!statusMatches(treatment)) return;
        if (doneServiceSessionsCount(treatment, PSY_SERVICE) === 0) return;
        if (!matchesOutcomeFilters(treatment, filters)) return;
        const creditsByPerson = new Map<string, PsyOutcomeCredit>();
        (treatment.theurapists_involved_in_treamtent || []).forEach((junction) => {
            const therapistUser = junction.technicians_id?.user_id;
            if (!therapistUser) return;
            creditsByPerson.set(therapistUser.id, {
                personKey: therapistUser.id,
                personName: normalizePersonName(therapistUser.first_name, therapistUser.last_name),
                personActive: therapistUser.status === ACTIVE_USER_STATUS,
                treatment,
            });
        });
        creditsByPerson.forEach((credit) => credits.push(credit));
    });
    return credits;
};

export const psyOutcomeCreditsMatching = (treatmentItems: Array<PsyOutcomeTreatmentItem>, filters: OutcomeFilters) => {
    const credits = psyCreditsMatchingByStatus(treatmentItems, filters, hasFinishedStatus);
    return credits;
};

export const activePsyCreditsMatching = (
    treatmentItems: Array<PsyOutcomeTreatmentItem>,
    filters: OutcomeFilters,
    spansByTreatment: SpansByTreatment
) => {
    const credits = psyCreditsMatchingByStatus(treatmentItems, filters, (treatment) =>
        hasActiveStatusOfService(treatment, spansByTreatment, PSY_SERVICE)
    );
    return credits;
};

// Âmbito de pessoas do pivot das psicólogas; restrictToPersonKey = vista individual (a fetch já vem
// filtrada ao tratamento dela, mas as colegas do mesmo tratamento têm de sair das linhas).
const psyPersonScope = (
    credits: Array<PsyOutcomeCredit>,
    includeSuspended: boolean,
    excludedPersonNames: Array<string>,
    restrictToPersonKey: string | null
) => {
    const scoped = credits.filter((credit) => {
        if (excludedPersonNames.includes(credit.personName)) return false;
        if (restrictToPersonKey !== null && credit.personKey !== restrictToPersonKey) return false;
        const included = includeSuspended || credit.personActive;
        return included;
    });
    return scoped;
};

export const scopedPsyOutcomeCredits = (
    treatmentItems: Array<PsyOutcomeTreatmentItem>,
    filters: OutcomeFilters,
    includeSuspended: boolean,
    excludedPersonNames: Array<string>,
    restrictToPersonKey: string | null
) => {
    const scoped = psyPersonScope(psyOutcomeCreditsMatching(treatmentItems, filters), includeSuspended, excludedPersonNames, restrictToPersonKey);
    return scoped;
};

export const scopedActivePsyCredits = (
    treatmentItems: Array<PsyOutcomeTreatmentItem>,
    filters: OutcomeFilters,
    includeSuspended: boolean,
    excludedPersonNames: Array<string>,
    restrictToPersonKey: string | null,
    spansByTreatment: SpansByTreatment
) => {
    const scoped = psyPersonScope(
        activePsyCreditsMatching(treatmentItems, filters, spansByTreatment),
        includeSuspended,
        excludedPersonNames,
        restrictToPersonKey
    );
    return scoped;
};

// Subsets de créditos por estado (drill por coluna: Ativos vs Terminados).
export const finishedPsyCreditsOf = (credits: Array<PsyOutcomeCredit>) => {
    const finished = credits.filter((credit) => hasFinishedStatus(credit.treatment));
    return finished;
};

export const activePsyCreditsOf = (credits: Array<PsyOutcomeCredit>, spansByTreatment: SpansByTreatment) => {
    const active = credits.filter((credit) => hasActiveStatusOfService(credit.treatment, spansByTreatment, PSY_SERVICE));
    return active;
};

// Célula "Total" do pivot das psicólogas: tratamentos DISTINTOS (um tratamento com 2 psicólogas
// aparece nas duas linhas mas conta uma vez no total).
export const distinctTreatmentsOfCredits = (credits: Array<PsyOutcomeCredit>) => {
    const byTreatmentId = new Map<string, PsyOutcomeTreatmentItem>();
    credits.forEach((credit) => byTreatmentId.set(String(credit.treatment.id), credit.treatment));
    const distinct = Array.from(byTreatmentId.values());
    return distinct;
};

// Medidas do pivot (células = arrays de tratamentos do nó).
export const successCountOf = (treatments: Array<OutcomeTreatmentBase>) => {
    const count = treatments.filter((treatment) => SUCCESS_STATUSES.includes(treatment.treatment_status_new!)).length;
    return count;
};

export const successPctOf = (treatments: Array<OutcomeTreatmentBase>) => {
    const successCount = successCountOf(treatments);
    const pct = successRatePct(successCount, treatments.length - successCount);
    return pct;
};

export const clientTypeTreatmentsOf = <TreatmentType extends OutcomeTreatmentBase>(treatments: Array<TreatmentType>, clientType: ClientTypeKey) => {
    const ofType = treatments.filter((treatment) => clientTypeOfAge(treatment.age_when_started) === clientType);
    return ofType;
};

export const avgDoneSessionsOf = (treatments: Array<OutcomeTreatmentBase>, service: number) => {
    if (treatments.length === 0) return null;
    const doneSessionsSum = treatments.reduce((sum, treatment) => sum + doneServiceSessionsCount(treatment, service), 0);
    const average = doneSessionsSum / treatments.length;
    return average;
};

export type OutcomeDrillRow = {
    id: number | string;
    clientNumber: string | null;
    personName: string;
    clientTypeLabel: string;
    disorderLabel: string;
    statusNew: string | null;
    dateOfStart: string | null;
    dateIfFinish: string | null;
    doneSessions: number;
};

// Linhas do drill a partir dos itens do próprio nó do pivot — âmbito bate com a célula por construção.
export const outcomeDrillRowOf = (treatment: OutcomeTreatmentBase, personName: string, doneSessionsService: number) => {
    const row: OutcomeDrillRow = {
        id: treatment.id,
        clientNumber: treatment.client?.client_id ?? null,
        personName,
        clientTypeLabel: CLIENT_TYPE_LABELS[clientTypeOfAge(treatment.age_when_started)],
        disorderLabel: disorderDrillLabelOf(treatment.diagnose_main),
        statusNew: treatment.treatment_status_new,
        dateOfStart: treatment.date_of_start,
        dateIfFinish: treatment.date_if_finish,
        doneSessions: doneServiceSessionsCount(treatment, doneSessionsService),
    };
    return row;
};

export const outcomeDrillRowsOf = (treatments: Array<OutcomeTreatmentItem>) => {
    const rows = treatments.map((treatment) => outcomeDrillRowOf(treatment, outcomeOwnerNameOf(treatment), NFB_SERVICE));
    return rows;
};
